import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState } from '../../app.service';
import { ToastHelperService } from '../../../services/toast-helper.service';
import { ProfileService } from '../../../services/profile.service';
import { ModalService } from '../../../services/modal.service';
import { AuthService } from '../../../services/auth.service';

function passwordRulesValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value || '';
  const errors: ValidationErrors = {};
  if (value.length < 6 || value.length > 12) errors['length'] = true;
  if (!/[a-z]/.test(value)) errors['lowercase'] = true;
  if (!/[A-Z]/.test(value)) errors['uppercase'] = true;
  if (!/\d/.test(value)) errors['digit'] = true;
  return Object.keys(errors).length ? errors : null;
}

/** MOCK_API: Replace with real IUserProfile from API */
export interface ChangePasswordModalData {
  user: {
    customerId?: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
  customClass?: string;
}

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './change-password-modal.component.html',
  styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent {
  step = 1;
  currentPasswordForm: FormGroup;
  newPasswordForm: FormGroup;
  showCurrentError = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  isNewPassFocused = false;

  get newPassword() {
    return this.newPasswordForm?.get('newPassword');
  }

  get passwordMismatch(): boolean {
    const newPassword = this.newPasswordForm?.controls?.['newPassword']?.value;
    const confirmNewPassword = this.newPasswordForm?.controls?.['confirmNewPassword']?.value;
    return !!newPassword && !!confirmNewPassword && newPassword !== confirmNewPassword;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChangePasswordModalData,
    private dialogRef: MatDialogRef<ChangePasswordModalComponent>,
    private appState: AppState,
    private toastHelper: ToastHelperService,
    private profileService: ProfileService,
    private modalService: ModalService,
    private translate: TranslateService,
    private authService: AuthService,
  ) {
    this.currentPasswordForm = new FormGroup({
      password: new FormControl('', [Validators.required])
    });
    this.newPasswordForm = new FormGroup({
      newPassword: new FormControl('', [Validators.required, passwordRulesValidator]),
      confirmNewPassword: new FormControl('', [Validators.required])
    });
  }

  submitCurrentPassword(): void {
    this.currentPasswordForm.markAllAsTouched();
    if (this.currentPasswordForm.invalid) {
      return;
    }
    this.appState.loading = true;
    const currentPassword = this.currentPasswordForm.controls['password'].value;
    this.authService.login(this.data.user.email, currentPassword).then(() => {
      this.appState.loading = false;
      this.step = 2;
    }, (err) => {
      const error = err.error.errors[0];
      this.appState.loading = false;
      if (error.type === 'Validation Error' || error?.textCode === 'INVALID_LOGIN_CREDENTIALS') {
        this.showCurrentError = true
      } else {
        this.toastHelper.error(error.message);
      }
    });
  }

  public updatePassword(): void {
    this.newPasswordForm.markAllAsTouched();
    if (this.newPasswordForm.invalid || this.passwordMismatch) {
      return;
    }

    this.appState.loading = true;
    const newPassword = this.newPasswordForm.controls['newPassword'].value;

    this.profileService.changePassword({ email: this.data.user.email, password: newPassword }).then((result) => {
      this.authService.login(this.data.user.email, newPassword).then(() => {
        this.appState.loading = false;
        this.closeDialog(true);
        this.modalService.successModal(
          {
            msg: this.translate.instant('success-modal.password-successfully-updated'),
            buttonText: this.translate.instant('success-modal.done'),
          }
        );
      }, (error) => {
        this.showError();
        this.appState.loading = false;
      });
    }, (error) => {
      this.showError();
      this.appState.loading = false;
    });
  }

  private showError() {
    this.modalService.failureModal(
      {
        msg: this.translate.instant('failure-modal.request-cannot-be-processed'),
        buttonText: this.translate.instant('failure-modal.got-it'),
        title: this.translate.instant('failure-modal.something-went-wrong'),
      }
    );
    this.appState.loading = false;
  }


  preventInputFocus(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?: boolean): void {
    this.beforeClose();
    this.dialogRef.close(res);
  }
}
